'use client';

import React from 'react';
import classnames from 'classnames';
import Box from '../../../atoms/Box/Box';
import Typography from '../../../atoms/Typography/Typography';
import Iconography from '../../../atoms/Iconography/Iconography';
import { SidebarIcon } from '../Icons';

import styles from './Navigation.module.scss';

interface IconConfig {
  category: string;
  name: string;
}

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: IconConfig;
}

interface Workspace {
  id: string;
  label: string;
  icon?: IconConfig;
  childrens: NavigationItem[];
}

interface NavigationProps {
  isExpanded: boolean;
  theme: 'light' | 'dark';
  selectedWorkspace: Workspace;
  selectedChildItem: string | null;
  onWorkspaceToggle: () => void;
  onChildItemClick: (itemId: string, path: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({
  isExpanded,
  theme,
  selectedWorkspace,
  selectedChildItem,
  onWorkspaceToggle,
  onChildItemClick,
}) => {
  const renderNavigationItem = (item: NavigationItem, isSelected: boolean, onClick: () => void) => (
    <button
      key={item.id}
      className={classnames(styles.childItem, {
        [styles.selected]: isSelected,
      })}
      onClick={onClick}
    >
      {item.icon && (
        <Box className={styles.iconWrapper}>
          <SidebarIcon
            name={item.icon.name}
            size={16}
            color={theme === 'dark' ? 'white' : 'black'}
          />
        </Box>
      )}
      {isExpanded && (
        <Typography variant="body" size="small" color="white" theme={theme}>
          {item.label}
        </Typography>
      )}
    </button>
  );

  return (
    <Box className={styles.navItems} pt={16} pb={16} data-theme={theme}>
      <Box className={styles.workspaceContainer}>
        <Typography variant="label" size="medium" color="gray-5" theme={theme}>
          {isExpanded ? 'Workspace' : '&nbsp;'}
        </Typography>
        <button className={styles.workspaceItem} onClick={onWorkspaceToggle}>
          <Box className={styles.workspaceLabelGroup}>
            {selectedWorkspace?.icon && (
              <Box className={styles.iconWrapper}>
                <SidebarIcon
                  name={selectedWorkspace.icon.name}
                  size={20}
                  color={theme === 'dark' ? 'white' : 'black'}
                />
              </Box>
            )}
            {isExpanded && (
              <Typography variant="subtitle" size="small" color="primary-1" theme={theme}>
                {selectedWorkspace?.label}
              </Typography>
            )}
          </Box>
          {isExpanded && (
            <Box className={styles.iconContainer}>
              <Iconography
                category="arrows"
                name="chevron-big-left"
                color={theme === 'dark' ? 'primary-1' : 'black'}
                size={20}
              />
              <Iconography
                category="arrows"
                name="chevron-big-right"
                color={theme === 'dark' ? 'primary-1' : 'black'}
                size={20}
              />
            </Box>
          )}
        </button>
      </Box>
      {selectedWorkspace.childrens.length > 0 && (
        <Box className={styles.childrenItems}>
          {selectedWorkspace.childrens.map((item) =>
            renderNavigationItem(item, selectedChildItem === item.id, () =>
              onChildItemClick(item.id, item.path)
            )
          )}
        </Box>
      )}
    </Box>
  );
};

Navigation.displayName = 'Navigation';

export default Navigation;
